import React, { Dispatch, SetStateAction } from 'react';
import { Modal } from '@dropbox/dig-components/modal';
import { Button } from '@dropbox/dig-components/buttons';
import { FormattedMessage } from 'react-intl';
import { Split } from '@dropbox/dig-foundations';
import styles from './billboard.module.scss';
import { errors, modalTypes } from '../constants';
import BillboardContent from './billboard-content';
import { BowlEmptyHero } from '@dropbox/dig-illustrations/dist/cjs/hero/bowl-empty';
import { PauseCancelModalContext, SafeDowngradeStatusEnum } from '../types';
import { PAP_Select_CancelSubscription } from '@dropbox/pap-events/sign_standalone/select_cancel_subscription';
import { cancelSubscription } from '../utils';
import { useLogPAPEvent } from 'hellospa/common/use-log-pap-event';

type BillboardProps = {
  setModalType: Dispatch<SetStateAction<string>>;
  setError: Dispatch<SetStateAction<string | null>>;
  handleCloseModal: () => void;
  modalContext: PauseCancelModalContext;
  currentStatus: SafeDowngradeStatusEnum | null;
};

const Billboard = ({
  setModalType,
  setError,
  handleCloseModal,
  modalContext,
  currentStatus,
}: BillboardProps) => {
  const logPAPEvent = useLogPAPEvent();
  const { planName, freePlanName } = modalContext;

  const restrictCancelNow =
    modalContext.siteCode === 'S' && !modalContext.isCaliforniaUser;

  // The user may confirm cancellation on the survey modal
  const handleOpenContinueCancelModal = () => {
    setModalType(modalTypes.SURVEY);
  };

  const handleOpenPauseModal = () => {
    setModalType(modalTypes.PAUSE);
  };

  const handleCancelNowClick = React.useCallback(async () => {
    const response = await cancelSubscription(modalContext.planToCancel);
    if (response.success) {
      logPAPEvent(
        PAP_Select_CancelSubscription({
          signCancelSubscriptionExperience:
            'pause_or_cancel_subscription_modal',
        }),
      );
      window.location.reload();
    } else {
      setError(errors.CANCELLATION);
      setModalType(modalTypes.ERROR);
    }
  }, [modalContext.planToCancel, logPAPEvent, setModalType, setError]);

  return (
    <div>
      <div className={styles.billboardImage}>
        <BowlEmptyHero aspectRatio="16:9" />
      </div>
      <BillboardContent planName={planName} freePlanName={freePlanName} />
      <Modal.Footer preferComposition>
        <Split gap="Micro Small">
          <Split.Item marginRight="auto">
            <Button
              variant="transparent"
              onClick={handleCloseModal}
              data-qa-ref="billboard-close-button"
            >
              <FormattedMessage
                id=""
                description="button, close modal button text"
                defaultMessage="Close"
              />
            </Button>
          </Split.Item>
          {currentStatus !== SafeDowngradeStatusEnum.PAUSED && (
            <Split.Item>
              <Button
                variant="outline"
                onClick={handleOpenPauseModal}
                data-qa-ref="billboard-pause-button"
              >
                <FormattedMessage
                  id=""
                  description="button, modal CTA to pause plan instead"
                  defaultMessage="Pause plan instead"
                />
              </Button>
            </Split.Item>
          )}
          <Split.Item>
            <Button
              variant="outline"
              onClick={handleOpenContinueCancelModal}
              data-qa-ref="billboard-cancel-with-feedback-button"
            >
              {restrictCancelNow ? (
                <FormattedMessage
                  id=""
                  description="new text for a button, modal CTA to cancel with feedback"
                  defaultMessage="Continue"
                />
              ) : (
                <FormattedMessage
                  id=""
                  description="button, modal CTA to cancel with feedback"
                  defaultMessage="Cancel with Feedback"
                />
              )}
            </Button>
          </Split.Item>
          {!restrictCancelNow && (
            <Split.Item>
              <Button
                variant="outline"
                onClick={handleCancelNowClick}
                data-qa-ref="billboard-cancel-now-button"
              >
                <FormattedMessage
                  id=""
                  description="button, modal CTA to cancel immediately"
                  defaultMessage="Cancel Now"
                />
              </Button>
            </Split.Item>
          )}
        </Split>
      </Modal.Footer>
    </div>
  );
};

export default Billboard;
